import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { POLL_INTERVAL_MS } from '../constants';
import type { Booking, Therapist, DashboardStats, CustomerData } from '../types';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function useAdminDashboard() {
  const navigate = useNavigate();
  const { user, token, logout, isAdmin, loading } = useAuth();

  // Core data
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');

  // In-app notification polling
  const [newBookingCount, setNewBookingCount] = useState(0);
  const [lastSeenBookingCount, setLastSeenBookingCount] = useState(null);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchData = useCallback(async () => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [statsRes, bookingsRes, therapistsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/dashboard`, { headers }),
        axios.get(`${API_URL}/api/admin/bookings`, { headers }),
        axios.get(`${API_URL}/api/admin/therapists`, { headers }),
      ]);
      if (statsRes) setStats(statsRes.data);
      if (bookingsRes) {
        setBookings(bookingsRes.data);
        const pendingCount = bookingsRes.data.filter(b => b.status === 'pending').length;
        if (lastSeenBookingCount === null) {
          setLastSeenBookingCount(pendingCount);
        } else if (pendingCount > lastSeenBookingCount) {
          setNewBookingCount(pendingCount - lastSeenBookingCount);
        }
      }
      if (therapistsRes) setTherapists(therapistsRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        logout();
        navigate('/login');
      }
    }
  }, [token, logout, navigate, lastSeenBookingCount]);

  useEffect(() => {
    if (loading) return;
    if (!token || !isAdmin) { navigate('/login'); return; }
    fetchData();
  }, [loading, token, isAdmin, fetchData, navigate]);

  useEffect(() => {
    if (!token || !isAdmin) return;
    const interval = setInterval(fetchData, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [token, isAdmin, fetchData]);

  const handleDismissNotification = () => {
    const pendingCount = bookings.filter(b => b.status === 'pending').length;
    setLastSeenBookingCount(pendingCount);
    setNewBookingCount(0);
  };

  return {
    user, token, logout, isAdmin,
    stats, bookings, therapists,
    activeTab, setActiveTab,
    newBookingCount, handleDismissNotification,
    authHeaders, fetchData,
    navigate,
  };
}

export function useAdminHandlers(token, fetchData) {
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Therapist management
  const [showAddTherapist, setShowAddTherapist] = useState(false);
  const [newTherapist, setNewTherapist] = useState({ name: '', phone_number: '', photo_url: '', description: '' });
  const [addingTherapist, setAddingTherapist] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showEditTherapist, setShowEditTherapist] = useState(false);
  const [editTherapistData, setEditTherapistData] = useState({ id: '', name: '', phone_number: '', photo_url: '', description: '', line_user_id: '' });
  const [savingTherapist, setSavingTherapist] = useState(false);
  const [uploadingEditPhoto, setUploadingEditPhoto] = useState(false);

  // Booking assignment
  const [assignDialog, setAssignDialog] = useState({ open: false, booking: null });
  const [selectedTherapist, setSelectedTherapist] = useState('');
  const [assigning, setAssigning] = useState(false);

  // Manual sending
  const [sendingManual, setSendingManual] = useState({});

  // Chat
  const [chatBooking, setChatBooking] = useState(null);

  const handleAddTherapist = async () => {
    if (!newTherapist.name || !newTherapist.phone_number) return;
    setAddingTherapist(true);
    try {
      await axios.post(`${API_URL}/api/admin/therapists`, { ...newTherapist, photo_url: newTherapist.photo_url || '/api/uploads/default-avatar.jpg' }, { headers: authHeaders });
      setShowAddTherapist(false);
      setNewTherapist({ name: '', phone_number: '', photo_url: '', description: '' });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to add therapist');
    } finally { setAddingTherapist(false); }
  };

  const handlePhotoUpload = async (e, isEdit = false) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    isEdit ? setUploadingEditPhoto(true) : setUploadingPhoto(true);
    try {
      const response = await axios.post(`${API_URL}/api/admin/upload-image`, formData, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' }
      });
      const photoUrl = response.data.photo_url;
      if (isEdit) setEditTherapistData(prev => ({ ...prev, photo_url: photoUrl }));
      else setNewTherapist(prev => ({ ...prev, photo_url: photoUrl }));
    } catch (error) {
      alert('Failed to upload photo');
    } finally { isEdit ? setUploadingEditPhoto(false) : setUploadingPhoto(false); }
  };

  const handleEditTherapist = (therapist) => {
    setEditTherapistData({
      id: therapist.id, name: therapist.name, phone_number: therapist.phone_number,
      photo_url: therapist.photo_url, description: therapist.description || '',
      line_user_id: therapist.line_user_id || ''
    });
    setShowEditTherapist(true);
  };

  const handleSaveTherapist = async () => {
    setSavingTherapist(true);
    try {
      await axios.put(`${API_URL}/api/admin/therapists/${editTherapistData.id}`, editTherapistData, { headers: authHeaders });
      setShowEditTherapist(false);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update therapist');
    } finally { setSavingTherapist(false); }
  };

  const handleDeactivateTherapist = async (id) => {
    if (!window.confirm('Are you sure you want to remove this therapist?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/therapists/${id}`, { headers: authHeaders });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to remove therapist');
    }
  };

  const handleGenerateOTP = async (therapist) => {
    try {
      const res = await axios.post(`${API_URL}/api/admin/therapists/${therapist.id}/generate-otp`, {}, { headers: authHeaders });
      const { otp, therapist_name, expires_in } = res.data;
      window.alert(`OTP for ${therapist_name}: ${otp}\n\nExpires in ${expires_in}.\nShare this code with the therapist to login.`);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to generate OTP');
    }
  };

  const handleToggleAvailability = async (therapistId) => {
    try {
      await axios.put(`${API_URL}/api/admin/therapists/${therapistId}/availability`, {}, { headers: authHeaders });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to toggle availability');
    }
  };

  const handleAssignTherapist = async () => {
    if (!assignDialog.booking || !selectedTherapist) return;
    setAssigning(true);
    try {
      await axios.put(`${API_URL}/api/admin/bookings/${assignDialog.booking.id}/assign`, { therapist_id: selectedTherapist }, { headers: authHeaders });
      setAssignDialog({ open: false, booking: null });
      setSelectedTherapist('');
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to assign therapist');
    } finally { setAssigning(false); }
  };

  const handleUpdateStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API_URL}/api/admin/bookings/${bookingId}/status`, { status }, { headers: authHeaders });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm('Permanently delete this booking?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/bookings/${bookingId}`, { headers: authHeaders });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete booking');
    }
  };

  const handleSendManualViaLine = async (therapistId, manualType, therapistName) => {
    const key = `${therapistId}_${manualType}`;
    setSendingManual(prev => ({ ...prev, [key]: true }));
    try {
      await axios.post(`${API_URL}/api/admin/send-manual-line`, { therapist_id: therapistId, manual_type: manualType }, { headers: authHeaders });
      const lang = manualType.endsWith('_en') ? 'English' : 'Thai';
      alert(`${lang} manual sent to ${therapistName} via LINE!`);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to send manual via LINE');
    } finally { setSendingManual(prev => ({ ...prev, [key]: false })); }
  };

  return {
    // Therapist add
    showAddTherapist, setShowAddTherapist,
    newTherapist, setNewTherapist,
    addingTherapist, uploadingPhoto,
    handleAddTherapist, handlePhotoUpload,
    // Therapist edit
    showEditTherapist, setShowEditTherapist,
    editTherapistData, setEditTherapistData,
    savingTherapist, uploadingEditPhoto,
    handleEditTherapist, handleSaveTherapist,
    handleDeactivateTherapist, handleGenerateOTP, handleToggleAvailability,
    // Booking assignment
    assignDialog, setAssignDialog,
    selectedTherapist, setSelectedTherapist,
    assigning, handleAssignTherapist,
    // Booking management
    handleUpdateStatus, handleDeleteBooking,
    // Manual
    sendingManual, handleSendManualViaLine,
    // Chat
    chatBooking, setChatBooking,
  };
}

export function usePasswordChange(token) {
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const handleChangePassword = async () => {
    if (!currentPassword) { setError('Please enter your current password'); return; }
    if (newPassword.length < 8) { setError('New password must be at least 8 characters'); return; }
    if (newPassword !== confirmPassword) { setError('New passwords do not match'); return; }
    if (currentPassword === newPassword) { setError('New password must be different from your current password'); return; }
    setLoading(true); setError('');
    try {
      await axios.post(`${API_URL}/api/auth/admin/change-password`,
        { current_password: currentPassword, new_password: newPassword }, { headers: authHeaders });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to change password');
    } finally { setLoading(false); }
  };

  const resetPasswordChangeDialog = () => {
    setShowPasswordChange(false);
    setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    setError(''); setDone(false);
  };

  return {
    showPasswordChange, setShowPasswordChange,
    currentPassword, setCurrentPassword,
    newPassword, setNewPassword,
    confirmPassword, setConfirmPassword,
    loading, error, done,
    handleChangePassword, resetPasswordChangeDialog,
  };
}

export function useAdminComputedData(bookings, therapists) {
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  // Revenue state
  const [revenuePeriod, setRevenuePeriod] = useState('weekly');
  const [revenueStartDate, setRevenueStartDate] = useState(null);
  const [revenueEndDate, setRevenueEndDate] = useState(null);
  const [revenueTherapistFilter, setRevenueTherapistFilter] = useState('all');

  // Calendar state
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarMonth, setCalendarMonth] = useState(new Date());

  // Customer state
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerStartDate, setCustomerStartDate] = useState(null);
  const [customerEndDate, setCustomerEndDate] = useState(null);

  // Date picker dialogs
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [datePickerMode, setDatePickerMode] = useState<'start' | 'end'>('start');
  const [showCustomerDatePicker, setShowCustomerDatePicker] = useState(false);
  const [customerDatePickerMode, setCustomerDatePickerMode] = useState<'start' | 'end'>('start');

  const selectedDateBookings = useMemo(() => {
    return bookings.filter(b => {
      const bd = new Date(b.created_at);
      return bd.toDateString() === selectedDate.toDateString();
    });
  }, [bookings, selectedDate]);

  const getDatesWithBookings = useMemo(() => {
    return Array.from(new Set<string>(bookings.map(b => new Date(b.created_at).toDateString()))).map(d => new Date(d));
  }, [bookings]);

  const revenueData = useMemo(() => {
    const now = new Date();
    let completedBookings = bookings.filter(b => b.status === 'completed');
    if (revenueTherapistFilter !== 'all') {
      completedBookings = completedBookings.filter(b =>
        b.assigned_therapist_id === revenueTherapistFilter || b.assigned_therapist_name === revenueTherapistFilter
      );
    }
    const getStartDate = (period) => {
      const date = new Date(now);
      if (period === 'weekly') date.setDate(date.getDate() - 7);
      else if (period === 'monthly') date.setMonth(date.getMonth() - 1);
      else if (period === 'yearly') date.setFullYear(date.getFullYear() - 1);
      else if (period === 'custom') return revenueStartDate ? new Date(revenueStartDate) : new Date(0);
      return date;
    };
    const getEndDate = (period) => {
      if (period === 'custom' && revenueEndDate) { const end = new Date(revenueEndDate); end.setHours(23, 59, 59, 999); return end; }
      return now;
    };
    const filterByPeriod = (period) => {
      if (period === 'allTime') return completedBookings;
      const startDate = getStartDate(period);
      const endDate = getEndDate(period);
      return completedBookings.filter(b => { const d = new Date(b.created_at); return d >= startDate && d <= endDate; });
    };
    const calculateStats = (filtered) => {
      const total = filtered.reduce((sum, b) => sum + (b.price_thb || 0), 0);
      const count = filtered.length;
      const avgPerBooking = count > 0 ? Math.round(total / count) : 0;
      const byTherapist = {};
      filtered.forEach(b => {
        const name = b.assigned_therapist_name || 'Unassigned';
        if (!byTherapist[name]) byTherapist[name] = { count: 0, revenue: 0 };
        byTherapist[name].count++;
        byTherapist[name].revenue += b.price_thb || 0;
      });
      const byService = {};
      filtered.forEach(b => {
        const service = b.service_type || 'Unknown';
        if (!byService[service]) byService[service] = { count: 0, revenue: 0 };
        byService[service].count++;
        byService[service].revenue += b.price_thb || 0;
      });
      return { total, count, avgPerBooking, byTherapist, byService, bookings: filtered };
    };
    return {
      weekly: calculateStats(filterByPeriod('weekly')),
      monthly: calculateStats(filterByPeriod('monthly')),
      yearly: calculateStats(filterByPeriod('yearly')),
      allTime: calculateStats(completedBookings),
      custom: calculateStats(filterByPeriod('custom'))
    };
  }, [bookings, revenueTherapistFilter, revenueStartDate, revenueEndDate]);

  const uniqueCustomers = useMemo((): CustomerData[] => {
    const map: Record<string, CustomerData> = {};
    bookings.forEach(b => {
      const key = b.contact_number || b.name;
      if (!map[key]) {
        map[key] = {
          name: b.name, contact_number: b.contact_number,
          line_id: b.line_id, whatsapp_number: b.whatsapp_number,
          bookings: [], totalSpent: 0, completedBookings: 0,
        };
      }
      map[key].bookings.push(b);
      if (b.status === 'completed') {
        map[key].totalSpent += b.price_thb || 0;
        map[key].completedBookings++;
      }
    });
    return Object.values(map).sort((a, b) => b.bookings.length - a.bookings.length);
  }, [bookings]);

  const filteredCustomers = useMemo(() => {
    if (!customerSearch) return uniqueCustomers;
    const s = customerSearch.toLowerCase();
    return uniqueCustomers.filter(c =>
      c.name?.toLowerCase().includes(s) || c.contact_number?.includes(s) ||
      c.line_id?.toLowerCase().includes(s) || c.whatsapp_number?.includes(s)
    );
  }, [uniqueCustomers, customerSearch]);

  const selectedCustomerBookings = useMemo(() => {
    if (!selectedCustomer) return [];
    let bks = [...selectedCustomer.bookings];
    if (customerStartDate) bks = bks.filter(b => new Date(b.created_at) >= new Date(customerStartDate));
    if (customerEndDate) {
      const end = new Date(customerEndDate); end.setHours(23, 59, 59);
      bks = bks.filter(b => new Date(b.created_at) <= end);
    }
    return bks.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [selectedCustomer, customerStartDate, customerEndDate]);

  const selectedCustomerStats = useMemo(() => {
    const completed = selectedCustomerBookings.filter(b => b.status === 'completed');
    return { count: selectedCustomerBookings.length, completed: completed.length, totalSpent: completed.reduce((s, b) => s + (b.price_thb || 0), 0) };
  }, [selectedCustomerBookings]);

  const currentRevenueData = revenueData[revenuePeriod];

  return {
    formatDate,
    // Revenue
    revenuePeriod, setRevenuePeriod,
    revenueStartDate, setRevenueStartDate,
    revenueEndDate, setRevenueEndDate,
    revenueTherapistFilter, setRevenueTherapistFilter,
    currentRevenueData,
    // Calendar
    selectedDate, setSelectedDate,
    calendarMonth, setCalendarMonth,
    selectedDateBookings, getDatesWithBookings,
    // Customers
    customerSearch, setCustomerSearch,
    selectedCustomer, setSelectedCustomer,
    customerStartDate, setCustomerStartDate,
    customerEndDate, setCustomerEndDate,
    uniqueCustomers, filteredCustomers,
    selectedCustomerBookings, selectedCustomerStats,
    // Date pickers
    showDatePicker, setShowDatePicker,
    datePickerMode, setDatePickerMode,
    showCustomerDatePicker, setShowCustomerDatePicker,
    customerDatePickerMode, setCustomerDatePickerMode,
  };
}
